/**
 * ConversationList Component
 * 
 * Displays all conversations for the current user.
 * Features:
 * - Real-time updates
 * - Pinned conversations at top (sorted by pin time)
 * - Context menu (right-click/long-press) for Pin/Unpin, Mark Read/Unread
 * - Last message preview
 * - Relative timestamps
 * - Unread indicators
 * - Search filtering (conversations + messages)
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Search, MessageSquarePlus, Pin, Users, X, Loader2, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useConversations } from './hooks/useConversations';
import { formatConversationTime } from './utils/timeFormat';
import { CONVERSATION_TYPES, COLLECTIONS } from '@/lib/firestore';
import { getConversationDisplayName } from '@/lib/firestore';
import ConversationContextMenu from './ConversationContextMenu';
import ArchiveScopeDialog from './ArchiveScopeDialog';
import { 
  ConversationResultItem, 
  MessageResultItem, 
  EmptySearchResults,
  SearchResultsSkeleton 
} from './SearchResultItem';
import { UserAvatar } from '@/components/shared';
import { stripHtml } from './utils/htmlUtils';
import { useDesign } from '@/contexts/DesignContext';

// For Firestore message search
import { collection, query, orderBy, getDocs, limit } from 'firebase/firestore';
import { getFirestoreDb } from '@/lib/firebase';

const db = () => getFirestoreDb();

// Debounce delay
const SEARCH_DEBOUNCE = 300;

/**
 * Avatar component for conversations - uses unified UserAvatar
 */
function ConversationAvatar({ userId, name, avatar, isGroup }) {
  if (isGroup) {
    // Group avatar with icon
    return (
      <div data-ui="avatar-group" className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white shadow-sm flex-shrink-0">
        <Users className="w-5 h-5" />
      </div>
    );
  }

  return (
    <UserAvatar 
      userId={userId}
      name={name}
      avatarUrl={avatar}
      size="lg"
      className="w-11 h-11 sm:w-12 sm:h-12 flex-shrink-0"
    />
  );
}

/**
 * Single conversation row
 */
function ConversationItem({ 
  conversation, 
  isSelected, 
  isPinned,
  displayName, 
  avatar,
  participantId,
  unreadCount,
  isManuallyUnread,
  onClick,
  onContextMenu
}) {
  const lastMessage = conversation.lastMessage;
  const isGroup = conversation.type === CONVERSATION_TYPES.GROUP;
  const longPressTimer = useRef(null);
  const isLongPress = useRef(false);
  
  // Show unread indicator if there are unread messages OR manually marked unread
  const showUnread = unreadCount > 0 || isManuallyUnread;
  
  // Format last message preview
  const getPreview = () => {
    if (!lastMessage) return 'No messages yet';
    
    const prefix = isGroup && lastMessage.senderName 
      ? `${lastMessage.senderName.split(' ')[0]}: ` 
      : '';
    
    if (lastMessage.type === 'image') return `${prefix}📷 Photo`;
    if (lastMessage.type === 'voice') return `${prefix}🎤 Voice message`;
    if (lastMessage.type === 'video') return `${prefix}🎬 Video`;
    if (lastMessage.type === 'poll') return `${prefix}📊 Poll`;
    
    return `${prefix}${stripHtml(lastMessage.text || '')}`;
  };

  // Handle touch start for long press
  const handleTouchStart = useCallback((e) => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      // Get touch position
      const touch = e.touches[0];
      onContextMenu({
        preventDefault: () => {},
        clientX: touch.clientX,
        clientY: touch.clientY
      });
    }, 500);
  }, [onContextMenu]);

  // Handle touch end
  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  }, []);

  // Handle click - only trigger if not long press
  const handleClick = useCallback(() => {
    if (!isLongPress.current) {
      onClick();
    }
    isLongPress.current = false;
  }, [onClick]);

  return (
    <button
      onClick={handleClick}
      onContextMenu={onContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      className={`w-full flex items-center gap-3 p-3 sm:p-3 rounded-xl transition-all text-left border-2 min-h-[72px] active:scale-[0.98]
        ${isSelected 
          ? 'bg-gradient-to-r from-blue-50 to-sky-50 dark:from-blue-500/15 dark:to-sky-500/15 border-blue-300 dark:border-blue-500/40 shadow-md' 
          : 'hover:bg-white dark:hover:bg-white/5 hover:shadow-md border-transparent active:bg-slate-50 dark:active:bg-white/5'
        }
        ${showUnread && !isSelected ? 'bg-blue-50/50 dark:bg-blue-500/10' : ''}
      `}
      data-ui="convo-row"
      data-selected={isSelected ? 'true' : undefined}
      data-unread={showUnread ? 'true' : undefined}
      data-testid={`conversation-item-${conversation.id}`}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <ConversationAvatar 
          userId={participantId}
          name={displayName} 
          avatar={avatar} 
          isGroup={isGroup} 
        />
        {showUnread && (
          <div data-ui="unread-badge" className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">
              {unreadCount > 99 ? '99+' : unreadCount > 0 ? unreadCount : '•'}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <span data-ui="convo-name" className={`truncate ${showUnread ? 'font-semibold text-slate-900 dark:text-slate-100' : 'font-medium text-slate-800 dark:text-slate-100'}`}>
            {displayName}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isPinned && <Pin data-ui="pin-ic" className="w-3 h-3 text-blue-500 dark:text-blue-400" />}
            <span data-ui="convo-time" className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
              {formatConversationTime(lastMessage?.timestamp || conversation.updatedAt)}
            </span>
          </div>
        </div>
        <p data-ui="convo-pv" className={`text-sm truncate mt-0.5 ${showUnread ? 'text-slate-700 dark:text-slate-200 font-medium' : 'text-slate-500 dark:text-slate-400'}`}>
          {getPreview()}
        </p>
      </div>
    </button>
  );
}

/**
 * Empty state when no conversations
 */
function EmptyState({ onNewConversation }) {
  return (
    <div data-ui="empty" className="flex flex-col items-center justify-center h-full p-8 text-center" data-testid="empty-conversations-state">
      <div data-ui="empty-glyph" className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center mb-5 shadow-inner">
        <MessageSquarePlus className="w-10 h-10 text-blue-500 dark:text-blue-400" />
      </div>
      <h3 data-ui="empty-title" className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">No conversations yet</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-[240px]">
        Start a new conversation to begin messaging with your team
      </p>
      <Button 
        onClick={onNewConversation} 
        className="bg-blue-500 hover:bg-blue-600 shadow-md hover:shadow-lg transition-all"
      >
        <MessageSquarePlus className="w-4 h-4 mr-2" />
        Start a conversation
      </Button>
    </div>
  );
}

/**
 * Loading skeleton with shimmer animation
 */
function LoadingSkeleton() {
  return (
    <div data-ui="skel-rows" className="space-y-2 p-3" data-testid="conversation-list-skeleton">
      {[1, 2, 3, 4, 5].map(i => (
        <div 
          key={i} 
          className="flex items-center gap-3 p-3 rounded-xl"
          style={{ animationDelay: `${i * 100}ms` }}
        >
          {/* Avatar skeleton */}
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] animate-shimmer" />
          
          {/* Content skeleton */}
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] animate-shimmer rounded-md w-3/4" 
                 style={{ animationDelay: `${i * 100 + 50}ms` }} />
            <div className="h-3 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] animate-shimmer rounded-md w-full"
                 style={{ animationDelay: `${i * 100 + 100}ms` }} />
          </div>
          
          {/* Time skeleton */}
          <div className="w-10 h-3 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] animate-shimmer rounded-md"
               style={{ animationDelay: `${i * 100 + 75}ms` }} />
        </div>
      ))}
    </div>
  );
}

/**
 * Get a snippet of text around the search match
 */
function getSnippet(text, searchQuery, contextLength = 40) {
  if (!text || !searchQuery) return text;
  
  const lowerText = text.toLowerCase();
  const lowerQuery = searchQuery.toLowerCase();
  const matchIndex = lowerText.indexOf(lowerQuery);
  
  if (matchIndex === -1) return text.slice(0, contextLength * 2);
  
  const start = Math.max(0, matchIndex - contextLength);
  const end = Math.min(text.length, matchIndex + searchQuery.length + contextLength);
  
  let snippet = text.slice(start, end);
  
  // Add ellipsis if truncated
  if (start > 0) snippet = '...' + snippet;
  if (end < text.length) snippet = snippet + '...';
  
  return snippet;
}

/**
 * Main ConversationList component
 */
export default function ConversationList({
  user,
  selectedConversationId,
  onSelectConversation,
  onNewConversation,
  onSearchResultSelect,
  unreadCounts = {}
}) {
  const { isStudio } = useDesign();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [messageSearchResults, setMessageSearchResults] = useState([]);
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    conversationId: null
  });
  // "Archive for everyone" scope choice (creator / church admin only)
  const [archiveScope, setArchiveScope] = useState({
    isOpen: false,
    conversationId: null,
    isArchiving: false
  });

  const { 
    conversations, 
    loading, 
    error,
    hasMoreConversations,
    loadingMore,
    loadMore,
    pin,
    unpin,
    isPinned,
    markAsRead,
    markAsUnread,
    archive,
    archiveForEveryone,
    isManuallyUnread,
    getDisplayName,
    getAvatar,
    getParticipantId
  } = useConversations(user?.id);

  // Load more conversations when scrolling near the bottom
  const listRef = useRef(null);
  const handleListScroll = useCallback(() => {
    if (!listRef.current || !hasMoreConversations || loadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMore();
    }
  }, [hasMoreConversations, loadingMore, loadMore]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, SEARCH_DEBOUNCE);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search messages when debounced query changes
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setMessageSearchResults([]);
      setIsSearching(false);
      return;
    }

    const searchMessages = async () => {
      setIsSearching(true);
      const results = [];
      const lowerQuery = debouncedQuery.toLowerCase();

      for (const conv of conversations) {
        try {
          const messagesRef = collection(db(), COLLECTIONS.CONVERSATIONS, conv.id, COLLECTIONS.MESSAGES);
          const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(100));
          const snapshot = await getDocs(q);

          snapshot.docs.forEach(doc => {
            const message = { id: doc.id, ...doc.data() };
            
            // Skip deleted messages
            if (message.deletedForEveryone || message.deletedFor?.includes(user?.id)) return;
            
            // Check if message text contains query
            if (message.text && stripHtml(message.text).toLowerCase().includes(lowerQuery)) {
              results.push({
                type: 'message',
                conversation: conv,
                message,
                displayName: getConversationDisplayName(conv, user?.id),
                snippet: getSnippet(stripHtml(message.text), debouncedQuery)
              });
            }
          });

          // Limit total results
          if (results.length >= 20) break;
        } catch (err) {
          console.error(`Error searching in conversation ${conv.id}:`, err);
        }
      }

      // Sort by timestamp
      results.sort((a, b) => {
        const timeA = a.message.createdAt?.toDate?.() || new Date(0);
        const timeB = b.message.createdAt?.toDate?.() || new Date(0);
        return timeB - timeA;
      });

      setMessageSearchResults(results.slice(0, 20));
      setIsSearching(false);
    };

    searchMessages();
  }, [debouncedQuery, conversations, user?.id]);

  // Filter conversations by search (name match)
  const filteredConversations = useMemo(() => {
    if (!(searchQuery || '').trim()) return conversations;
    
    const query = searchQuery.toLowerCase();
    return conversations.filter(conv => {
      const name = getDisplayName(conv).toLowerCase();
      const lastMessage = stripHtml(conv.lastMessage?.text || '').toLowerCase();
      return name.includes(query) || lastMessage.includes(query);
    });
  }, [conversations, searchQuery, getDisplayName]);

  // Handle right-click context menu
  const handleContextMenu = useCallback((e, conversationId) => {
    e.preventDefault();
    
    // Calculate position to ensure menu stays within viewport
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 150);
    
    setContextMenu({
      isOpen: true,
      position: { x, y },
      conversationId
    });
  }, []);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Handle pin from context menu
  const handlePin = useCallback(() => {
    if (contextMenu.conversationId) {
      pin(contextMenu.conversationId);
    }
  }, [contextMenu.conversationId, pin]);

  // Handle unpin from context menu
  const handleUnpin = useCallback(() => {
    if (contextMenu.conversationId) {
      unpin(contextMenu.conversationId);
    }
  }, [contextMenu.conversationId, unpin]);

  // Handle mark as read from context menu
  const handleMarkRead = useCallback(() => {
    if (contextMenu.conversationId) {
      markAsRead(contextMenu.conversationId);
    }
  }, [contextMenu.conversationId, markAsRead]);

  // Handle mark as unread from context menu
  const handleMarkUnread = useCallback(() => {
    if (contextMenu.conversationId) {
      markAsUnread(contextMenu.conversationId);
    }
  }, [contextMenu.conversationId, markAsUnread]);

  // Handle archive from context menu.
  // Church owners/admins and the chat's creator get to choose the scope
  // ("just for me" vs "for everyone"); everyone else archives immediately,
  // exactly as before.
  const handleArchive = useCallback(() => {
    const conversationId = contextMenu.conversationId;
    if (!conversationId) return;
    const conversation = conversations.find(c => c.id === conversationId);
    const isChurchLeader = user?.role === 'owner' || user?.role === 'admin';
    const isCreator = !!user?.id && conversation?.createdBy === user.id;
    if (isChurchLeader || isCreator) {
      setArchiveScope({ isOpen: true, conversationId, isArchiving: false });
      return;
    }
    archive(conversationId);
  }, [contextMenu.conversationId, conversations, user?.role, user?.id, archive]);

  const closeArchiveScope = useCallback(() => {
    setArchiveScope({ isOpen: false, conversationId: null, isArchiving: false });
  }, []);

  const handleArchiveForMe = useCallback(() => {
    if (archiveScope.conversationId) {
      archive(archiveScope.conversationId);
    }
    closeArchiveScope();
  }, [archiveScope.conversationId, archive, closeArchiveScope]);

  const handleArchiveForEveryone = useCallback(async () => {
    const conversationId = archiveScope.conversationId;
    if (!conversationId) return;
    setArchiveScope(prev => ({ ...prev, isArchiving: true }));
    const ok = await archiveForEveryone(conversationId);
    // On failure the chat is left exactly as it was; the hook has toasted why.
    if (ok) {
      closeArchiveScope();
    } else {
      setArchiveScope(prev => ({ ...prev, isArchiving: false }));
    }
  }, [archiveScope.conversationId, archiveForEveryone, closeArchiveScope]);

  // Handle message search result click
  const handleMessageResultClick = useCallback((result) => {
    // Select the conversation and pass the message to scroll to
    if (onSearchResultSelect) {
      onSearchResultSelect(result.conversation, result.message, searchQuery);
    } else {
      onSelectConversation(result.conversation);
    }
  }, [onSearchResultSelect, onSelectConversation, searchQuery]);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setDebouncedQuery('');
    setMessageSearchResults([]);
  }, []);

  // Get context menu state
  const contextConversation = contextMenu.conversationId 
    ? conversations.find(c => c.id === contextMenu.conversationId)
    : null;
  const contextIsPinned = contextMenu.conversationId ? isPinned(contextMenu.conversationId) : false;
  const contextHasUnread = contextMenu.conversationId 
    ? (unreadCounts[contextMenu.conversationId] > 0 || isManuallyUnread(contextMenu.conversationId))
    : false;

  // Timeout: if loading takes too long, show connection error with diagnostics
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  useEffect(() => {
    if (loading) {
      setLoadingTimedOut(false);
      const timer = setTimeout(() => setLoadingTimedOut(true), 8000);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Determine what to show
  const isSearchActive = searchQuery.trim().length > 0;
  const hasNoResults = isSearchActive && filteredConversations.length === 0 && messageSearchResults.length === 0;

  if (loading && loadingTimedOut) {
    const diagnosticInfo = JSON.stringify({
      step: 'messaging_load',
      userId: user?.id?.slice(0, 8) + '...',
      firebaseAuth: typeof window !== 'undefined' && window.__fbAuthStatus || 'unknown',
      browser: navigator.userAgent.slice(0, 80),
      timestamp: new Date().toISOString()
    }, null, 2);

    return (
      <div className="h-full flex flex-col bg-card md:border-r md:border-slate-200">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-sm text-center space-y-4">
            <div data-ui="empty-glyph" data-tone="warn" className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto">
              <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 data-ui="empty-title" className="text-lg font-semibold text-slate-800 dark:text-slate-100" data-testid="messaging-connection-error">
              Unable to load messages
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              This is usually caused by a browser extension or network issue. Try refreshing, or copy the info below and send to support.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                data-testid="messaging-refresh-btn"
              >
                {isStudio ? 'Refresh page' : 'Refresh Page'}
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`Messaging Error\n\n${diagnosticInfo}\n\nPlease send to support@worshipos.app`);
                  toast.success('Copied to clipboard');
                }}
                className="px-4 py-2 bg-slate-100 dark:bg-[#3a3d41] text-slate-700 dark:text-slate-200 text-sm rounded-lg hover:bg-slate-200 dark:hover:bg-[#4a4d51] transition-colors"
                data-testid="messaging-copy-error-btn"
              >
                {isStudio ? 'Copy error info' : 'Copy Error Info'}
              </button>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              support@worshipos.app
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-card md:border-r md:border-slate-200">
        <div className="p-3 sm:p-4 border-b border-slate-100 dark:border-white/10">
          <div className="h-10 bg-slate-100 dark:bg-[#3a3d41] rounded-lg animate-pulse" />
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-card md:border-r md:border-slate-200 overflow-hidden w-full max-w-full">
      {/* Header */}
      <div data-ui="list-head" className="px-3 pt-5 pb-3 sm:p-4 border-b border-slate-100 dark:border-white/10 w-full max-w-full box-border">
        {/* Title row - hidden on mobile since tab already shows "Messages" */}
        <div className="hidden md:flex items-center justify-between mb-3">
          <h2 data-ui="list-title" className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100">Messages</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onNewConversation}
            data-ui="ibtn"
            className="text-blue-500 dark:text-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/15 w-10 h-10 flex-shrink-0"
            data-testid="new-conversation-btn"
          >
            <MessageSquarePlus className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 z-10" />
          <Input
            type="text"
            placeholder={isStudio ? 'Search conversations and messages' : 'Search conversations & messages...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-9 pr-9 bg-slate-50 dark:bg-[#1e1f22] border-slate-200 dark:border-white/10 focus:bg-white dark:focus:bg-[#2d2f33] h-11 text-base w-full box-border ${isStudio ? 'studio-search' : ''}`}
            data-testid="conversation-search-input"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              data-ui="search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 p-1"
              data-testid="clear-search-btn"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {hasNoResults && !isSearching ? (
        <EmptySearchResults searchQuery={searchQuery} />
      ) : isSearchActive ? (
        // Search Results View - show results immediately, with loading indicator for messages
        <div className="flex-1 overflow-y-auto">
          {/* Conversation matches - always show immediately */}
          {filteredConversations.length > 0 && (
            <div className="p-3">
              <h3 data-ui="eyebrow" className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 px-2">
                Conversations
              </h3>
              <div className="space-y-1">
                {filteredConversations.map(conversation => (
                  <ConversationResultItem
                    key={conversation.id}
                    result={{
                      conversation,
                      displayName: getDisplayName(conversation)
                    }}
                    searchQuery={searchQuery}
                    isSelected={selectedConversationId === conversation.id}
                    onClick={() => onSelectConversation(conversation)}
                    currentUserId={user?.id}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Message matches - with inline loading indicator */}
          <div className="p-3">
            <div className="flex items-center justify-between mb-2 px-2">
              <h3 data-ui="eyebrow" className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Messages
              </h3>
              {isSearching && (
                <Loader2 className="w-3 h-3 animate-spin text-slate-400 dark:text-slate-500" />
              )}
            </div>
            {messageSearchResults.length > 0 ? (
              <div className="space-y-1">
                {messageSearchResults.map((result, idx) => (
                  <MessageResultItem
                    key={`${result.message.id}-${idx}`}
                    result={result}
                    searchQuery={searchQuery}
                    onClick={() => handleMessageResultClick(result)}
                  />
                ))}
              </div>
            ) : !isSearching ? (
              <p className="text-sm text-slate-400 dark:text-slate-500 px-2">No message matches</p>
            ) : null}
          </div>
        </div>
      ) : filteredConversations.length === 0 ? (
        <EmptyState onNewConversation={onNewConversation} />
      ) : (
        // Normal Conversation List
        <div ref={listRef} className="flex-1 overflow-y-auto" onScroll={handleListScroll}>
          <div data-ui="convo-rows" className="p-3 space-y-1.5">
            {filteredConversations.map(conversation => {
              // Never show unread badge for the currently selected/active conversation
              const isActive = selectedConversationId === conversation.id;
              return (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isSelected={isActive}
                  isPinned={isPinned(conversation.id)}
                  displayName={getDisplayName(conversation)}
                  avatar={getAvatar(conversation)}
                  participantId={getParticipantId(conversation)}
                  unreadCount={isActive ? 0 : (unreadCounts[conversation.id] || 0)}
                  isManuallyUnread={isActive ? false : isManuallyUnread(conversation.id)}
                  onClick={() => onSelectConversation(conversation)}
                  onContextMenu={(e) => handleContextMenu(e, conversation.id)}
                />
              );
            })}
            {loadingMore && (
              <div className="flex justify-center py-3">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400 dark:text-slate-500" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Context Menu */}
      <ConversationContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        isPinned={contextIsPinned}
        hasUnread={contextHasUnread}
        onPin={handlePin}
        onUnpin={handleUnpin}
        onMarkRead={handleMarkRead}
        onMarkUnread={handleMarkUnread}
        onArchive={handleArchive}
        onClose={closeContextMenu}
      />

      {/* Archive scope choice (chat creator / church admins) */}
      <ArchiveScopeDialog
        isOpen={archiveScope.isOpen}
        isArchiving={archiveScope.isArchiving}
        onClose={closeArchiveScope}
        onArchiveForMe={handleArchiveForMe}
        onArchiveForEveryone={handleArchiveForEveryone}
      />

      {/* Error display */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-950/40 border-t border-red-100">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  );
}
